import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu as MenuIcon, X as XIcon, Map, FileText, BookOpen, Calculator, Megaphone, Sprout, PenTool, CloudRain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppLogo from '@/assets/logo.svg?react';
import { getAppUrl, getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  const handleMobileLinkClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  
  const appLoginUrl = getAppUrl('/auth');
  const appSignUpUrl = getSignupUrl();

  const resources = [
      { title: "State Compliance Directory", href: "/swppp-requirements", icon: Map, desc: "Find specific permits and regulations for your state." },
      { title: "Rain Event Finder", href: "/tools/rain-event-finder", icon: CloudRain, desc: "Check historical rainfall for missed inspections." },
      { title: "Map Sketcher", href: "/tools/map-sketcher", icon: PenTool, desc: "Draw SWPPP site plans online." },
      { title: "BMP Field Guide", href: "/resources/bmp-field-guide", icon: BookOpen, desc: "Encyclopedia of erosion and sediment controls." },
      { title: "Free Templates", href: "/resources/templates", icon: FileText, desc: "Downloadable PDF inspection forms." },
      { title: "Toolbox Talks", href: "/resources/toolbox-talks", icon: Megaphone, desc: "Safety meeting scripts for site crews." },
      { title: "Fine Risk Calculator", href: "/tools/fine-calculator", icon: Calculator, desc: "Estimate potential EPA penalties." },
      { title: "Stabilization Visualizer", href: "/resources/vegetation-density", icon: Sprout, desc: "Visual guide to 70% vegetative cover." },
  ];

  return (
    <>
    <a href="#main-content" className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition-transform focus:translate-y-0">Skip to content</a>
    <header className="sticky top-0 z-50 w-full border-b border-border/70 bg-background/95 backdrop-blur-md print:hidden">
      <div className="container flex h-[4.5rem] items-center justify-between gap-5">
        
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="RunOffTrack home">
          <div className="rounded-xl border border-primary/15 bg-primary/5 p-1.5 shadow-sm">
            <AppLogo className="h-7 w-7" aria-hidden="true" />
          </div>
          <span className="text-lg font-extrabold tracking-[-0.035em] text-foreground sm:text-xl">RunOffTrack</span>
        </Link>

        <div className="hidden md:block">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <Link to="/features" className={navigationMenuTriggerStyle() + " bg-transparent text-muted-foreground hover:text-primary"}>
                  Features
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link to="/solutions/for-consultants" className={navigationMenuTriggerStyle() + " bg-transparent text-muted-foreground hover:text-primary"}>
                  For Consultants
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link to="/pricing" className={navigationMenuTriggerStyle() + " bg-transparent text-muted-foreground hover:text-primary"}>
                  Pricing
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-muted-foreground hover:text-primary">Resources</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[42rem] grid-cols-2 gap-1 p-3">
                    {resources.map((s) => (
                      <li key={s.title}>
                        <NavigationMenuLink asChild>
                           <Link to={s.href} className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/5 hover:text-primary focus:bg-primary/5 focus:text-primary">
                             <div className="text-sm font-medium leading-none flex items-center gap-2">
                                <s.icon size={16} /> {s.title}
                             </div>
                             <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1">
                               {s.desc}
                             </p>
                           </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center gap-1 md:flex">
              <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <a href={appLoginUrl}>Login</a>
              </Button>
              <Button asChild size="sm">
                <a href={appSignUpUrl} onClick={() => trackSignupIntent('header_desktop')}>Try the app</a>
              </Button>
          </div>

          <div className="md:hidden">
            <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} className="h-10 w-10 bg-background">
                  {mobileMenuOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="mt-2 max-h-[80vh] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl p-2 shadow-xl">
                <DropdownMenuItem onClick={() => handleMobileLinkClick('/features')}>Features</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleMobileLinkClick('/solutions/for-consultants')}>For Consultants</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleMobileLinkClick('/pricing')}>Pricing</DropdownMenuItem>
                <DropdownMenuSeparator className="my-2"/>
                <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">Resources</div>
                {resources.map(s => (
                    <DropdownMenuItem key={s.title} onClick={() => handleMobileLinkClick(s.href)} className="pl-4">
                        {s.title}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="my-2"/>
                <DropdownMenuItem onClick={() => { window.location.href = appLoginUrl; }}>Login</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { trackSignupIntent('header_mobile'); window.location.href = appSignUpUrl; }} className="font-semibold text-primary">Try the app</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
